/**
 * Module 'cloud' (ADR 0106 decisions 3, 3a, 4 and 5): VMs Orgus creates in a person's own cloud
 * account, and their lifecycle. AWS first.
 *
 * A workspace connects AWS once: its owner or an admin creates an IAM role (the CloudFormation
 * template apps/control-plane/src/ext/cloud/aws-orgus-role.yaml, through a quick-create link) that
 * trusts Orgus's own AWS principal with an external id unique to the workspace. Orgus assumes it
 * with STS AssumeRole for every call. The role can only start, stop, reboot and terminate
 * instances tagged `orgus:workspace=<workspace id>`, and can only create instances carrying that
 * tag. No AWS key of the customer's is ever held.
 *
 * Routes (control plane, module 'cloud'):
 *
 *   GET    /api/cloud                         CloudState                                     anyone signed in
 *   (/api/cloud/aws/... routes: any provider id works in place of aws, ADR 0109.)
 *   POST   /api/cloud/aws/setup               { region? } -> CloudConnection (the external id, the quick-create link)   owner, admin
 *   POST   /api/cloud/aws/connect             { roleArn, region } -> tests and saves the role                         owner, admin
 *   POST   /api/cloud/aws/test                -> CloudConnection with test                                             owner, admin
 *   DELETE /api/cloud/aws                     disconnect (VMs keep running; Orgus stops controlling them)              owner, admin
 *   POST   /api/cloud/vms                     CloudVmCreate -> CloudVm (201)                                           operator and up
 *   POST   /api/cloud/vms/:id/stop|start|restart|keep                                                                operator and up
 *   PUT    /api/cloud/vms/:id/rules           { idleStopMinutes: number | null }                                       operator and up
 *   DELETE /api/cloud/vms/:id                 { waitMinutes?, now? } terminate and remove from Orgus                    operator and up
 *   POST   /api/cloud/vms/:id/wake            { timeoutSeconds? } -> CloudWakeResult: start and wait until online       operator and up
 *   GET    /api/cloud/aws/instances           -> CloudDiscovery: instances in the region that run this workspace's agent  owner, admin
 *   POST   /api/cloud/aws/adopt               { instanceIds } -> CloudAdoptResult: Orgus controls them from now on     owner, admin
 *
 * Browser-safe, no dependencies.
 */
import type { Id, Ts } from '../index.ts';
import type { CloudProviderId, CloudProviderInfo } from './cloud-provider.ts';

export const CLOUD_EXT = 'cloud';

/** The provider a connection or VM belongs to (ADR 0109: any registered provider id). */
export type CloudProvider = CloudProviderId;
export type CloudVmLifetime = 'ephemeral' | 'permanent';

/** The tags every resource Orgus makes carries; the role's conditions read `orgus:workspace`. */
export const CLOUD_TAG_WORKSPACE = 'orgus:workspace';
export const CLOUD_TAG_VM = 'orgus:vm';
export const CLOUD_TAG_LIFETIME = 'orgus:lifetime';
/**
 * The tag a person puts on an instance they made by hand so Orgus may adopt it (ADR 0106
 * amendment 24 Oct 2026): the role may tag an instance for the workspace only when it carries
 * `orgus=fleet` and no `orgus:workspace` tag yet.
 */
export const CLOUD_TAG_FLEET_KEY = 'orgus';
export const CLOUD_TAG_FLEET_VALUE = 'fleet';
/** How long a wake waits for the machine to come online by default, and at most. */
export const CLOUD_WAKE_TIMEOUT_MS = 5 * 60_000;
export const CLOUD_WAKE_TIMEOUT_MAX_MS = 15 * 60_000;
/** Instances one adopt call takes. */
export const CLOUD_ADOPT_MAX = 20;

/** ADR 0106 3a defaults. */
export const CLOUD_IDLE_LIMIT_MS = 30 * 60_000;
export const CLOUD_MAX_LIFETIME_MS = 8 * 3600_000;
/** How long Stop and Delete wait for running work on the disabled machine before they act. */
export const CLOUD_DRAIN_WAIT_MS = 10 * 60_000;
export const CLOUD_DRAIN_WAIT_MAX_MS = 60 * 60_000;
/** A VM's join ticket lives this long (ADR 0105 decision 2: a provider's boot can be slow). */
export const CLOUD_VM_TICKET_TTL_MS = 60 * 60_000;
/** VMs one workspace may have at once (a guard against a runaway script, not a plan limit). */
export const CLOUD_VMS_MAX = 20;

/** The latest Ubuntu 24.04 LTS AMI for amd64, from Canonical's public SSM parameter. */
export const AWS_UBUNTU_AMI_PARAMETER = '/aws/service/canonical/ubuntu/server/24.04/stable/current/amd64/hvm/ebs-gp3/ami-id';
/** The security group Orgus makes (or reuses) in the default VPC: no inbound rule at all. */
export const AWS_SECURITY_GROUP_NAME = 'orgus-no-inbound';
/** The stack name the quick-create link proposes. */
export const AWS_ROLE_STACK_NAME = 'orgus-access';
export const AWS_ROLE_ARN_RE = /^arn:aws:iam::(\d{12}):role\/[\w+=,.@/-]{1,512}$/;
export const AWS_INSTANCE_ID_RE = /^i-[0-9a-f]{8,17}$/;

export interface CloudVmSize {
  id: string;
  vcpus: number;
  memoryGib: number;
  /** One line for the size picker. */
  use: string;
  /**
   * Eligible on the AWS Free Tier plan (accounts made after 15 Jul 2025): such an account can run
   * only eligible types, and AWS refuses any other with InvalidParameterCombination.
   */
  freeTier?: boolean;
}

export const AWS_VM_SIZES: readonly CloudVmSize[] = [
  { id: 't3.small', vcpus: 2, memoryGib: 2, use: 'One light session', freeTier: true },
  { id: 't3.medium', vcpus: 2, memoryGib: 4, use: 'One session with builds' },
  { id: 't3.large', vcpus: 2, memoryGib: 8, use: 'Two sessions, bigger repos' },
  { id: 'c7i-flex.large', vcpus: 2, memoryGib: 4, use: 'One session with builds', freeTier: true },
  { id: 'm7i-flex.large', vcpus: 2, memoryGib: 8, use: 'Two sessions, bigger repos', freeTier: true },
  { id: 'm7i.xlarge', vcpus: 4, memoryGib: 16, use: 'Several sessions, heavy builds' },
];
export const AWS_DEFAULT_SIZE = 't3.medium';

/** The error code Orgus answers when the AWS account is on the Free Tier plan and the size is not eligible. */
export const CLOUD_ERROR_FREE_TIER = 'aws-free-tier';

/** True when the size runs on an AWS Free Tier plan account. */
export function awsFreeTierEligible(size: string): boolean {
  return AWS_VM_SIZES.some((s) => s.id === size && s.freeTier === true);
}

/**
 * The closest Free Tier size to one AWS refused on a Free Tier plan account: the eligible size
 * with the same memory or more (capped at 4 GiB, so big sizes land on c7i-flex.large) and the
 * least above it. Null when the size is already eligible.
 */
export function awsFreeTierFallback(size: string): string | null {
  if (awsFreeTierEligible(size)) return null;
  const want = AWS_VM_SIZES.find((s) => s.id === size);
  const ok = AWS_VM_SIZES.filter((s) => s.freeTier === true);
  const need = Math.min(want?.memoryGib ?? 4, 4);
  const fit = ok.filter((s) => s.memoryGib >= need).sort((a, b) => a.memoryGib - b.memoryGib || a.vcpus - b.vcpus)[0];
  return (fit ?? ok[0])?.id ?? null;
}

export const AWS_REGIONS: readonly { id: string; label: string }[] = [
  { id: 'us-east-1', label: 'US East (N. Virginia)' },
  { id: 'us-east-2', label: 'US East (Ohio)' },
  { id: 'us-west-2', label: 'US West (Oregon)' },
  { id: 'eu-west-1', label: 'Europe (Ireland)' },
  { id: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { id: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
];
export const AWS_DEFAULT_REGION = 'us-east-1';

/** The disk every VM Orgus creates gets: 20 GB of encrypted gp3, deleted with the VM. */
export const AWS_DISK_GB = 20;

/**
 * Estimates shown on a VM's card and drawer (ADR 0106 decision 5, bring your own cloud). AWS bills
 * the customer; Orgus charges nothing for compute. `usdPerHour`: on-demand Linux, shared tenancy.
 * `gp3UsdPerGbMonth`: EBS gp3 storage, which a VM pays while running AND while stopped (a stopped
 * VM keeps its disk). Data transfer is not included.
 *
 * Checked 24 Oct 2026 against AWS's public price list (the files behind
 * aws.amazon.com/ec2/pricing/on-demand and aws.amazon.com/ebs/pricing, which load their tables
 * dynamically): us-east-1 m7i.xlarge 0.2016 and t3 per-vCPU rates match, gp3 0.08 in us-east-1
 * and us-west-2. The other regions' rows are AWS's published rates as of Oct 2026, not re-read
 * line by line. c7i-flex.large and m7i-flex.large (added 25 Oct 2026 for Free Tier plan
 * accounts): us-east-1 0.08479 and 0.09576, rounded; the other regions scale by that region's m7i
 * rate, an estimate until re-read. Re-check each quarter.
 */
export const AWS_PRICES = {
  asOf: '2026-10-24',
  source: 'AWS public price list: EC2 On-Demand Linux (aws.amazon.com/ec2/pricing/on-demand) and EBS gp3 (aws.amazon.com/ebs/pricing)',
  usdPerHour: {
    'us-east-1': { 't3.small': 0.0208, 't3.medium': 0.0416, 't3.large': 0.0832, 'c7i-flex.large': 0.0848, 'm7i-flex.large': 0.0958, 'm7i.xlarge': 0.2016 },
    'us-east-2': { 't3.small': 0.0208, 't3.medium': 0.0416, 't3.large': 0.0832, 'c7i-flex.large': 0.0848, 'm7i-flex.large': 0.0958, 'm7i.xlarge': 0.2016 },
    'us-west-2': { 't3.small': 0.0208, 't3.medium': 0.0416, 't3.large': 0.0832, 'c7i-flex.large': 0.0848, 'm7i-flex.large': 0.0958, 'm7i.xlarge': 0.2016 },
    'eu-west-1': { 't3.small': 0.0228, 't3.medium': 0.0456, 't3.large': 0.0912, 'c7i-flex.large': 0.0939, 'm7i-flex.large': 0.106, 'm7i.xlarge': 0.2232 },
    'eu-central-1': { 't3.small': 0.024, 't3.medium': 0.048, 't3.large': 0.096, 'c7i-flex.large': 0.0969, 'm7i-flex.large': 0.1094, 'm7i.xlarge': 0.2304 },
    'ap-south-1': { 't3.small': 0.0224, 't3.medium': 0.0448, 't3.large': 0.0896, 'c7i-flex.large': 0.0848, 'm7i-flex.large': 0.0958, 'm7i.xlarge': 0.2016 },
  } as Record<string, Record<string, number>>,
  gp3UsdPerGbMonth: {
    'us-east-1': 0.08,
    'us-east-2': 0.08,
    'us-west-2': 0.08,
    'eu-west-1': 0.088,
    'eu-central-1': 0.0952,
    'ap-south-1': 0.0912,
  } as Record<string, number>,
};

/** AWS bills EBS by GB-month; 730 hours make a month. */
const HOURS_PER_MONTH = 730;

/** What a stopped VM still costs a month: its disk. Null when the table has no price. */
export function awsDiskUsdPerMonth(region: string, gb: number = AWS_DISK_GB): number | null {
  const p = AWS_PRICES.gp3UsdPerGbMonth[region];
  return p === undefined ? null : Math.round(p * gb * 100) / 100;
}

/** The disk's share of an hour, added to the running estimate. */
export function awsDiskUsdPerHour(region: string, gb: number = AWS_DISK_GB): number | null {
  const p = AWS_PRICES.gp3UsdPerGbMonth[region];
  return p === undefined ? null : (p * gb) / HOURS_PER_MONTH;
}

/** The hourly estimate for a size in a region, or null when the table does not have it. */
export function awsHourlyUsd(size: string, region: string): number | null {
  return AWS_PRICES.usdPerHour[region]?.[size] ?? null;
}

export const isAwsSize = (s: unknown): s is string => typeof s === 'string' && AWS_VM_SIZES.some((x) => x.id === s);
export const isAwsRegion = (s: unknown): s is string => typeof s === 'string' && AWS_REGIONS.some((x) => x.id === s);

/** A workspace's AWS connection as the console sees it. Nothing here is secret. */
export interface CloudConnection {
  provider: CloudProvider;
  /** 'pending': the external id exists, no role saved yet. */
  state: 'pending' | 'connected';
  externalId: string;
  /** The workspace id the role's conditions name (`orgus:workspace`). */
  workspaceId: string;
  /** Orgus's own AWS principal the role trusts (empty when the server has none set). */
  principalArn: string;
  roleArn: string | null;
  accountId: string | null;
  region: string;
  /** The CloudFormation quick-create link, or null when the server has no template URL set. */
  quickCreateUrl: string | null;
  connectedBy: string | null;
  connectedAt: Ts | null;
  test: { at: Ts; ok: boolean; message: string } | null;
}

/** Where a VM stands, from Orgus's last provider call or DescribeInstances. */
export type CloudVmState = 'pending' | 'running' | 'stopping' | 'stopped' | 'shutting-down' | 'terminated';
/** An operation Orgus is carrying out: waiting for work on the disabled machine, then the call. */
export type CloudVmOp = 'stop' | 'delete' | 'start' | 'restart';

export interface CloudVm {
  id: Id;
  provider: CloudProvider;
  name: string;
  instanceId: string;
  region: string;
  size: string;
  lifetime: CloudVmLifetime;
  state: CloudVmState;
  /** The machine once it joined (by its join ticket, or by name). */
  machineId: Id | null;
  /** The public repository the VM cloned at boot, or null. */
  repoUrl: string | null;
  createdBy: string;
  createdAt: Ts;
  /** Ephemeral: deleted when idle this long, and at createdAt + maxLifetimeMs at the latest. */
  idleLimitMs: number;
  maxLifetimeMs: number;
  /** Permanent: stopped when idle this long; null (the default) never. */
  idleStopMs: number | null;
  /** Since when nothing has run on the machine (null: something runs, or not joined yet). */
  idleSince: Ts | null;
  /** An operation under way: what, since when, and until when it waits for running work. */
  op: { kind: CloudVmOp; by: string; at: Ts; waitUntil: Ts | null } | null;
  hourlyUsd: number | null;
  /** The last provider refusal, in plain words (what, why, next joined), until the next success. */
  error: string | null;
  updatedAt: Ts;
  /** Set when Orgus adopted an instance a person made (it did not create it). Additive. */
  adoptedAt?: Ts;
  /** The instance's private DNS name (ip-172-31-...), which the machine reports as its host name. */
  privateDns?: string;
  /**
   * Set when Orgus created the VM at another size than asked: the AWS account is on the Free Tier
   * plan and refused the size, so Orgus retried once with the closest eligible one. Additive.
   */
  sizeChange?: { requested: string; reason: 'aws-free-tier'; message: string };
}

/** An instance in the connected region that runs (or may run) this workspace's agent. */
export interface CloudInstanceCandidate {
  instanceId: string;
  region: string;
  /** Its Name tag, or null. */
  name: string | null;
  size: string;
  state: CloudVmState;
  privateDns: string | null;
  /** The machine in this workspace whose host name matches the instance's private DNS name. */
  machineId: Id | null;
  machineName: string | null;
  /** Why it is listed: it carries orgus=fleet, this workspace's tag, or its host name matches a machine. */
  match: ('fleet-tag' | 'workspace-tag' | 'host-name')[];
  /** Whether the role may take it over now; when false, `blocked` says what to do. */
  adoptable: boolean;
  blocked: string | null;
}

export interface CloudDiscovery {
  region: string;
  at: Ts;
  candidates: CloudInstanceCandidate[];
}

export interface CloudAdoptResult {
  adopted: CloudVm[];
  failed: { instanceId: string; error: string }[];
}

/**
 * What a wake answers (CloudModule.wakeMachine, POST /api/cloud/vms/:id/wake): `online` when the
 * machine reports within the timeout (`started` false when it was already running).
 */
export type CloudWakeResult =
  | { ok: true; vmId: Id; machineId: Id; started: boolean; waitedMs: number }
  | { ok: false; reason: 'not-a-vm' | 'not-connected' | 'busy' | 'timeout' | 'refused'; vmId: Id | null; machineId: Id | null; waitedMs: number; error: { what: string; why: string; next: string } };

/** The machine whose host name is the instance's private DNS name (ip-172-31-1-2 or ip-172-31-1-2.ec2.internal). */
export function sameAwsHost(hostname: string | null | undefined, privateDns: string | null | undefined): boolean {
  const a = (hostname ?? '').trim().toLowerCase().split('.')[0];
  const b = (privateDns ?? '').trim().toLowerCase().split('.')[0];
  return !!a && !!b && a === b;
}

export interface CloudState {
  connections: CloudConnection[];
  vms: CloudVm[];
  /** False when the server cannot create VMs (no Orgus AWS principal configured): why. */
  provisioner: { ready: boolean; problem: string | null };
  /** Every provider this server offers, with its catalogue and readiness (ADR 0109). */
  providers?: CloudProviderInfo[];
}

export interface CloudVmCreate {
  name?: string;
  size?: string;
  region?: string;
  lifetime?: CloudVmLifetime;
  /** The connected provider to create in; default the workspace's first connected one. */
  provider?: CloudProvider;
  /** A public https git repository the VM clones for the agent before it joins (cloud-init). */
  repoUrl?: string;
}

/** The CloudFormation quick-create link for the role (the template must be in S3, AWS requires it). */
export function awsQuickCreateUrl(o: { templateUrl: string; region: string; externalId: string; workspaceId: string; principalArn: string }): string {
  const q = Object.entries({
    templateURL: o.templateUrl,
    stackName: AWS_ROLE_STACK_NAME,
    param_ExternalId: o.externalId,
    param_WorkspaceId: o.workspaceId,
    param_OrgusPrincipalArn: o.principalArn,
  })
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return `https://${o.region}.console.aws.amazon.com/cloudformation/home?region=${o.region}#/stacks/quickcreate?${q}`;
}

/** The status a VM's machine card shows (ADR 0106 decision 1), or null when the machine's own status applies. */
export type CloudVmStatus = 'starting' | 'stopping' | 'stopped' | 'deleting' | null;

export function cloudVmStatus(vm: Pick<CloudVm, 'state' | 'op' | 'machineId'>, machineOnline: boolean): CloudVmStatus {
  if (vm.op?.kind === 'delete' || vm.state === 'shutting-down' || vm.state === 'terminated') return 'deleting';
  if (vm.op?.kind === 'stop' || vm.state === 'stopping') return 'stopping';
  if (vm.state === 'stopped') return 'stopped';
  if (vm.op?.kind === 'start' || vm.op?.kind === 'restart' || vm.state === 'pending') return 'starting';
  // Running at the provider but not reporting yet: still starting (cloud-init, the install, pairing).
  if (vm.state === 'running' && (!vm.machineId || !machineOnline)) return 'starting';
  return null;
}

const mins = (ms: number) => Math.max(1, Math.ceil(ms / 60_000));
const span = (ms: number) => (ms >= 3600_000 ? `${Math.floor(ms / 3600_000)}h ${mins(ms % 3600_000) % 60}m`.replace(/ 0m$/, '') : `${mins(ms)}m`);
const clock = (t: Ts) => {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** When an ephemeral VM ends, at the earliest: the idle deadline (when idle) or the lifetime cap. */
export function cloudVmEndsAt(vm: Pick<CloudVm, 'lifetime' | 'createdAt' | 'idleSince' | 'idleLimitMs' | 'maxLifetimeMs'>): { at: Ts; why: 'idle' | 'lifetime' } | null {
  if (vm.lifetime !== 'ephemeral') return null;
  const cap = vm.createdAt + vm.maxLifetimeMs;
  if (vm.idleSince !== null && vm.idleSince + vm.idleLimitMs < cap) return { at: vm.idleSince + vm.idleLimitMs, why: 'idle' };
  return { at: cap, why: 'lifetime' };
}

/** One line on the card: how the VM will end ("Deleted in 22m if idle", "Ends at 18:40"), or null. */
export function cloudVmEnding(vm: Pick<CloudVm, 'lifetime' | 'createdAt' | 'idleSince' | 'idleLimitMs' | 'maxLifetimeMs' | 'idleStopMs' | 'state'>, now: Ts): string | null {
  if (vm.state === 'terminated' || vm.state === 'shutting-down') return null;
  if (vm.lifetime === 'permanent') return vm.idleStopMs && vm.idleSince !== null && vm.state === 'running' ? `Stops in ${span(vm.idleSince + vm.idleStopMs - now)} if idle` : null;
  const end = cloudVmEndsAt(vm);
  if (!end) return null;
  return end.why === 'idle' ? `Deleted in ${span(end.at - now)} if idle` : `Ends at ${clock(end.at)}`;
}

